import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { Check, X, Clock, FileText, Mail, Phone, RefreshCw } from 'lucide-react';

export default function AdminDemandesFacturesPage() {
  const { api } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/facture-request/pending');
      setRequests(res.data.requests || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action) => {
    setActionLoading(id);
    try {
      await api.post(`/facture-request/${action}/${id}`);
      setRequests(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Layout>
    <div className="space-y-6" data-testid="admin-demandes-factures">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Demandes de factures</h1>
          <p className="text-gray-400 text-sm mt-1">
            Demandes de changement d'adresse email en attente de validation
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="border-gray-600 text-gray-300" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Rafraichir
        </Button>
      </div>

      {loading ? (
        <div className="text-center text-gray-400 py-12">Chargement...</div>
      ) : requests.length === 0 ? (
        <Card className="bg-[#12122a] border-gray-800">
          <CardContent className="py-12 text-center">
            <Clock className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">Aucune demande en attente</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((req) => (
            <Card key={req.id} className="bg-[#12122a] border-gray-800" data-testid={`request-${req.id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center justify-between text-base">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-yellow-500" />
                    {req.ref_vente}
                  </div>
                  <span className="text-sm text-white font-medium">{req.total?.toFixed(2)} EUR</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-400 text-sm">{req.items_summary}</p>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs mb-1">Client</p>
                    <p className="text-white">{req.nom}</p>
                    <div className="flex items-center gap-1 text-gray-400 mt-1">
                      <Phone className="w-3 h-3" /> {req.telephone}
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs mb-1">Changement d'email</p>
                    <div className="flex items-center gap-1 text-red-400">
                      <Mail className="w-3 h-3" /> {req.original_email}
                    </div>
                    <div className="flex items-center gap-1 text-green-400 mt-1">
                      <Mail className="w-3 h-3" /> {req.new_email}
                    </div>
                  </div>
                </div>

                <div className="text-gray-500 text-xs">
                  Demande le {formatDate(req.created_at)}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    data-testid={`approve-${req.id}`}
                    onClick={() => handleAction(req.id, 'approve')}
                    disabled={actionLoading === req.id}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    size="sm"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    {actionLoading === req.id ? '...' : 'Approuver'}
                  </Button>
                  <Button
                    data-testid={`reject-${req.id}`}
                    onClick={() => handleAction(req.id, 'reject')}
                    disabled={actionLoading === req.id}
                    variant="outline"
                    className="flex-1 border-red-600 text-red-400 hover:bg-red-600/10"
                    size="sm"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Refuser
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
    </Layout>
  );
}
